import { useMemo, useState } from "react";
import {
  sectionQuality,
  LEVEL_COLORS,
} from "@/components/dossier/dossier_quality";

/**
 * Badge de calidad de una sección del dossier.
 *
 * Muestra un indicador 🟢🟡🔴 inline con el nº de slides generados y
 * un panel desplegable con la lista de warnings detectados por el linter.
 *
 * Se monta bajo el header de cada sección en DossierTab.jsx.
 */
export default function SectionQualityBadge({ section }) {
  const [open, setOpen] = useState(false);
  const result = useMemo(() => sectionQuality(section), [section]);
  const palette = LEVEL_COLORS[result.level] || LEVEL_COLORS.ok;

  if (result.level === "empty") {
    return null; // No mostrar nada para secciones vacías
  }

  const summary = result.slides === 1
    ? "1 slide"
    : `${result.slides} slides paginados`;

  return (
    <div className="relative inline-flex"
         data-testid={`section-quality-${section.key}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Calidad de presentación"
        className={`flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] transition ${palette.bg} ${palette.border} ${palette.text} hover:shadow-sm`}
      >
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: palette.dot }} />
        <span>{summary}</span>
        {result.warnings.length > 0 && (
          <span className="rounded-full bg-white/70 px-1.5 text-[9px]">
            {result.warnings.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-7 z-30 w-96 rounded-lg border border-stone-200 bg-white p-3 shadow-xl"
             onMouseLeave={() => setOpen(false)}>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-500">
              Calidad de presentación · {palette.label}
            </p>
            <button onClick={() => setOpen(false)}
                    className="text-stone-400 hover:text-stone-700">✕</button>
          </div>

          <p className="mb-2 text-[11px] text-stone-600">
            Esta sección generará <strong>{result.slides}</strong> {result.slides === 1 ? "slide" : "slides"} con la plantilla <em>{result.effectiveLayout}</em>.
          </p>

          {result.warnings.length === 0 ? (
            <p className="rounded bg-emerald-50 px-2 py-1.5 text-[11px] text-emerald-800">
              ✓ Sin avisos. La sección está lista para presentar.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {result.warnings.map((w, i) => {
                const wp = LEVEL_COLORS[w.level] || LEVEL_COLORS.warn;
                return (
                  <li key={i}
                      className={`rounded border ${wp.border} ${wp.bg} px-2 py-1.5 text-[11px] ${wp.text}`}>
                    {w.message}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
